import React, {useState} from 'react';
import {Modal, StyleSheet, View} from 'react-native';
import {useMutation} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {rotateCameraSecret} from '@/entities/stream/api';
import {CameraCredentialsCard} from '@/features/add-camera/ui/CameraCredentialsCard';
import {messageOf} from '@/shared/lib/errors';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * A new secret for a camera added by hand — when the old one was lost or leaked.
 * Asks first: the camera keeps working with the old one only until it next
 * connects (12 hours at most), so a new secret must also be pasted into it.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(3)},
  backdrop: {flex: 1, justifyContent: 'center', padding: spacing(5)},
  dialog: {borderRadius: radii.xl, padding: spacing(5), gap: spacing(4)},
  row: {flexDirection: 'row', gap: spacing(2)},
  flex: {flex: 1},
});

export function CameraSecretPanel({
  companyId,
  sourceId,
}: {
  companyId: string;
  sourceId: string;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const [confirm, setConfirm] = useState(false);
  const rotate = useMutation({
    mutationFn: () => rotateCameraSecret(companyId, sourceId),
    onSuccess: () => setConfirm(false),
  });

  return (
    <View style={styles.root}>
      {rotate.data ? (
        <CameraCredentialsCard credentials={rotate.data} />
      ) : (
        <AppButton
          title={t('camera.secret.rotate')}
          tone="outline"
          onPress={() => setConfirm(true)}
        />
      )}
      {rotate.isError ? (
        <Banner tone="error" title={messageOf(rotate.error, t('errors.unknown'))} />
      ) : null}

      <Modal
        visible={confirm}
        transparent
        animationType="fade"
        onRequestClose={() => setConfirm(false)}>
        <View style={[styles.backdrop, {backgroundColor: p.overlay}]}>
          <View style={[styles.dialog, {backgroundColor: p.elevated}]}>
            <AppText variant="title">{t('camera.secret.confirmTitle')}</AppText>
            <AppText variant="body" color={p.mutedForeground}>
              {t('camera.secret.confirmBody')}
            </AppText>
            <View style={styles.row}>
              <AppButton
                title={t('actions.cancel')}
                tone="outline"
                style={styles.flex}
                onPress={() => setConfirm(false)}
              />
              <AppButton
                title={t('camera.secret.rotate')}
                style={styles.flex}
                loading={rotate.isPending}
                onPress={() => rotate.mutate()}
              />
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}
